/**
 * The Deliveries screen's Totals.
 *
 * The finance summary every delivery report already uses, under the same
 * channel, country, state and location the list is narrowed to — so the
 * money on this tab is the money behind the rows on the other.
 *
 * Each figure is its own line and none is folded into another. What customers
 * paid is not what the business kept; what riders earned is not business
 * revenue; work done and not yet paid is owed, not earned. And one block per
 * currency: a business trading in Lagos and Accra has two totals, because
 * naira and cedis added together are not an amount of anything.
 */

import type { ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import type { DeliveryFinanceFigures, DeliveryFinanceSummary, DeliveryOrigin } from '@cenox/shared';
import { api, qs } from '../../lib/api';
import { useAuth, useBusinessPath } from '../../lib/auth';
import { useLocationReady } from '../../lib/locationContext';
import { count, date, money, todayIn } from '../../lib/format';
import { ErrorState, Spinner } from '../../components/ui';

export interface TotalsScope {
  originSource?: DeliveryOrigin;
  countryCode?: string;
  stateCode?: string;
  branchId?: string;
}

const LINES: Array<{
  key: keyof Omit<DeliveryFinanceFigures, 'jobs'>;
  label: string;
  hint: string;
  strong?: boolean;
}> = [
  {
    key: 'revenue',
    label: 'Gross delivery value',
    hint: 'Fees on completed, paid jobs',
  },
  {
    key: 'companyCommission',
    label: 'Business commission',
    hint: 'Gross value less rider earnings — what the business kept',
    strong: true,
  },
  {
    key: 'riderEarnings',
    label: 'Rider earnings',
    hint: 'Owed to riders on those jobs; not business revenue',
  },
  { key: 'riderPaid', label: 'Paid to riders', hint: 'Covered by settlements marked paid' },
  { key: 'riderOwed', label: 'Still owed to riders', hint: 'Earnings not yet settled' },
  {
    key: 'riderHeldCash',
    label: 'Cash held by riders',
    hint: 'Fares riders collected in cash and have not settled',
  },
  {
    key: 'receivable',
    label: 'Unpaid deliveries',
    hint: 'Completed work not yet paid for — owed, not revenue',
  },
];

export function DeliveryTotals({
  scope,
  from,
  to,
  onRange,
}: {
  scope: TotalsScope;
  from: string;
  to: string;
  onRange: (from: string, to: string) => void;
}): ReactNode {
  const { business } = useAuth();
  const path = useBusinessPath();
  const locationReady = useLocationReady();
  const timezone = business?.timezone ?? 'UTC';

  // A total needs a period. Without one chosen, the month so far — in the
  // business's own calendar, not the browser's.
  const today = todayIn(timezone);
  const period = {
    from: from || `${today.slice(0, 8)}01`,
    to: to || today,
  };

  const summary = useQuery({
    queryKey: ['deliveries', 'finance-summary', business?.businessId, period, scope],
    queryFn: () =>
      api.get<DeliveryFinanceSummary>(
        path(`/deliveries/finance-summary${qs({ ...period, ...scope })}`),
      ),
    enabled: Boolean(business) && locationReady,
    staleTime: 30_000,
  });

  const data = summary.data;
  // Older API responses carry no per-currency split; they were one currency.
  const blocks =
    data?.byCurrency ??
    (data ? [{ currencyCode: business?.currencyCode ?? 'NGN', ...data.total }] : []);

  return (
    <section className="space-y-4">
      <div className="rounded-xl border border-slate-200 bg-white p-4 shadow-sm">
        <div className="flex flex-wrap items-end gap-3">
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">From</span>
            <input
              type="date"
              value={period.from}
              max={period.to}
              onChange={(e) => onRange(e.target.value, period.to)}
              className="input-base h-10"
            />
          </label>
          <label className="block">
            <span className="mb-1 block text-xs font-medium text-slate-500">To</span>
            <input
              type="date"
              value={period.to}
              min={period.from}
              onChange={(e) => onRange(period.from, e.target.value)}
              className="input-base h-10"
            />
          </label>
          <p className="pb-2 text-xs text-slate-500">
            Dated by when each job happened. The source, country and state above apply here too.
          </p>
        </div>
      </div>

      {summary.isLoading && <Spinner label="Loading totals" />}
      {summary.isError && (
        <ErrorState message="Could not load delivery totals." onRetry={() => void summary.refetch()} />
      )}

      {data && blocks.length === 0 && (
        <div className="rounded-xl border border-slate-200 bg-white px-4 py-10 text-center shadow-sm">
          <p className="text-sm font-medium text-slate-900">No deliveries found.</p>
          <p className="mt-1 text-sm text-slate-500">Try adjusting your filters or the period.</p>
        </div>
      )}

      {blocks.map((block) => (
        <article
          key={block.currencyCode}
          className="rounded-xl border border-slate-200 bg-white shadow-sm"
        >
          <header className="flex items-baseline justify-between gap-3 px-4 pb-2 pt-4">
            <h2 className="text-base font-semibold text-slate-900">
              Totals{blocks.length > 1 ? ` · ${block.currencyCode}` : ''}
            </h2>
            <span className="text-xs text-slate-500">
              {date(period.from, 'UTC')} – {date(period.to, 'UTC')} · {count(block.jobs)} paid{' '}
              {block.jobs === 1 ? 'job' : 'jobs'}
            </span>
          </header>
          <dl className="divide-y divide-slate-100">
            {LINES.map((line) => (
              <div key={line.key} className="flex items-start justify-between gap-4 px-4 py-3">
                <dt>
                  <div className={line.strong ? 'text-sm font-semibold text-slate-900' : 'text-sm text-slate-800'}>
                    {line.label}
                  </div>
                  <div className="text-xs text-slate-500">{line.hint}</div>
                </dt>
                <dd
                  className={
                    line.strong
                      ? 'whitespace-nowrap text-base font-semibold tabular-nums text-green-700'
                      : 'whitespace-nowrap text-sm font-medium tabular-nums text-slate-900'
                  }
                >
                  {money(block[line.key], block.currencyCode)}
                </dd>
              </div>
            ))}
          </dl>
        </article>
      ))}

      {data && data.byLocation.length > 1 && (
        <article className="rounded-xl border border-slate-200 bg-white shadow-sm">
          <h2 className="px-4 pb-2 pt-4 text-base font-semibold text-slate-900">By location</h2>
          <div className="overflow-x-auto">
            <table className="w-full min-w-[480px] text-sm">
              <thead>
                <tr className="border-y border-slate-200 bg-slate-50 text-left text-xs text-slate-500">
                  <th className="px-4 py-2 font-medium">Location</th>
                  <th className="px-4 py-2 text-right font-medium">Paid jobs</th>
                  <th className="px-4 py-2 text-right font-medium">Gross</th>
                  <th className="px-4 py-2 text-right font-medium">Commission</th>
                  <th className="px-4 py-2 text-right font-medium">Unpaid</th>
                </tr>
              </thead>
              <tbody>
                {data.byLocation.map((row) => {
                  const cur = row.currencyCode ?? business?.currencyCode ?? 'NGN';
                  return (
                    <tr key={row.branchId} className="border-b border-slate-100 last:border-0">
                      <td className="px-4 py-2.5">{row.branchName ?? '—'}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{count(row.jobs)}</td>
                      <td className="px-4 py-2.5 text-right tabular-nums">{money(row.revenue, cur)}</td>
                      <td className="px-4 py-2.5 text-right font-medium tabular-nums">
                        {money(row.companyCommission, cur)}
                      </td>
                      <td className="px-4 py-2.5 text-right tabular-nums text-slate-500">
                        {money(row.receivable, cur)}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </article>
      )}
    </section>
  );
}
